/**
 * Sniper v2 — the latency HUD (docs/260909SniperV2Design.md §6).
 *
 * "Every keypress has a measured budget, shown on screen" (design principle
 * 5). Both measurement ends already exist; this component only RENDERS them:
 *
 *   browser  `lt2Timing` ring — inputAtMs → guardMs → ackMs → requestMs →
 *            responseMs → renderMs → finalMs (wall clock, per submission)
 *   relay    `Server-Timing` stages parsed by `serverTiming.ts` and attached
 *            to the `orderLatencyProbe` ring — recv → venue_sent →
 *            venue_acked → responded (cumulative ms since relay receipt)
 *
 * The strip, exactly §6:
 *
 *   key→POST 3 ms │ POST→venue_sent 11 ms │ venue RTT 41 ms │ ack→paint 2 ms │ total 57 ms   p50 62 · p95 140 · n 84
 *
 * Budget: ≤80 ms total green, 80–150 amber, >150 red (node1).
 *
 * Lane liveness (tick age, lane candidate, adverse-guard state) arrives as
 * PROPS, never as store reads: the band owns that wiring, so this component
 * stays renderable from a fixture and carries no second authority over the
 * lane's health.
 *
 * The two rings are written by the same submit path in the same order, so they
 * are paired from the TAIL. A row whose relay half is missing (ORDER_TIMING
 * off, a refusal that never reached the wire) renders those cells as '—' and
 * contributes nothing to its total — an honest gap, never an invented number.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { cn } from '@/lib/infra/utils';
import { lt2TimingEntries, type Lt2TimingEntry } from '@/lib/trading/lt2Timing';
import { orderLatencySamples, type OrderLatencySample } from '@/lib/trading/orderLatencyProbe';

/** §6 budget, in ms of total. */
export const LATENCY_BUDGET_GREEN_MS = 80;
export const LATENCY_BUDGET_AMBER_MS = 150;
/** The `L` dump is the last 50 entries (§6). */
export const LATENCY_CSV_LIMIT = 50;

export type LatencyTone = 'green' | 'amber' | 'red' | 'none';

export interface LatencyRow {
  inputMethod: string;
  outcome: string;
  /** keydown/click → the POST leaving the store. */
  keyToPostMs: number | null;
  /** Relay receipt → the order on the wire to the venue. */
  postToVenueSentMs: number | null;
  /** venue_acked − venue_sent. */
  venueRttMs: number | null;
  /** Local ack → the first frame that could show it. */
  ackToPaintMs: number | null;
  /** Sum of the available components — the number the budget colours. */
  totalMs: number | null;
}

function diff(a: number | null | undefined, b: number | null | undefined): number | null {
  if (typeof a !== 'number' || typeof b !== 'number') return null;
  const d = a - b;
  return Number.isFinite(d) ? d : null;
}

function stage(
  stages: Record<string, number> | null | undefined,
  name: string,
): number | null {
  const v = stages?.[name];
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

/** Build one HUD row from a browser entry plus (optionally) its relay stages. */
export function latencyRowFrom(
  entry: Lt2TimingEntry,
  stages?: Record<string, number> | null,
): LatencyRow {
  const keyToPostMs = diff(entry.requestMs, entry.inputAtMs);
  const recv = stage(stages, 'recv') ?? (stages ? 0 : null);
  const postToVenueSentMs = diff(stage(stages, 'venue_sent'), recv);
  const venueRttMs = diff(stage(stages, 'venue_acked'), stage(stages, 'venue_sent'));
  const ackToPaintMs = diff(entry.renderMs, entry.ackMs);
  const parts = [keyToPostMs, postToVenueSentMs, venueRttMs, ackToPaintMs].filter(
    (v): v is number => v != null,
  );
  return {
    inputMethod: entry.inputMethod,
    outcome: entry.outcome,
    keyToPostMs,
    postToVenueSentMs,
    venueRttMs,
    ackToPaintMs,
    totalMs: parts.length > 0 ? parts.reduce((a, b) => a + b, 0) : null,
  };
}

/**
 * Pair the two session rings and project HUD rows (oldest → newest).
 *
 * Only SUBMITTED entries (they reached the wire, so `requestMs` exists) can
 * carry relay stages; refusals and throttles are kept as browser-only rows so
 * the `n` on screen matches what the trader actually pressed.
 */
export function collectLatencyRows(
  entries: readonly Lt2TimingEntry[] = lt2TimingEntries(),
  samples: readonly OrderLatencySample[] = orderLatencySamples(),
): LatencyRow[] {
  const submitted = entries.filter((e) => e.requestMs != null);
  const timed = samples.filter((s) => s.serverTiming != null);
  // Tail-aligned: both rings append per submission, and the probe ring is the
  // shorter one (50 vs 200). Offset is the count of submissions with no probe
  // sample left to pair with.
  const offset = submitted.length - timed.length;
  const stagesFor = new Map<Lt2TimingEntry, Record<string, number>>();
  submitted.forEach((entry, i) => {
    const j = i - offset;
    const sample = j >= 0 ? timed[j] : undefined;
    if (sample?.serverTiming) stagesFor.set(entry, sample.serverTiming);
  });
  return entries.map((e) => latencyRowFrom(e, stagesFor.get(e) ?? null));
}

function percentile(sorted: number[], p: number): number | null {
  if (sorted.length === 0) return null;
  const idx = Math.min(sorted.length - 1, Math.ceil((p / 100) * sorted.length) - 1);
  return sorted[Math.max(0, idx)];
}

export interface LatencySummary {
  p50: number | null;
  p95: number | null;
  /** Rows with a total — what p50/p95 were computed over. */
  n: number;
  latest: LatencyRow | null;
}

/** p50 / p95 / n over the session ring's totals (§6). */
export function summarizeLatency(rows: readonly LatencyRow[]): LatencySummary {
  const totals = rows
    .map((r) => r.totalMs)
    .filter((v): v is number => v != null)
    .sort((a, b) => a - b);
  return {
    p50: percentile(totals, 50),
    p95: percentile(totals, 95),
    n: totals.length,
    latest: rows.length > 0 ? rows[rows.length - 1] : null,
  };
}

/** ≤80 green, 80–150 amber, >150 red. 80 is green, 150 is amber (§6). */
export function latencyBudgetTone(totalMs: number | null | undefined): LatencyTone {
  if (totalMs == null || !Number.isFinite(totalMs)) return 'none';
  if (totalMs <= LATENCY_BUDGET_GREEN_MS) return 'green';
  if (totalMs <= LATENCY_BUDGET_AMBER_MS) return 'amber';
  return 'red';
}

const TONE_CLASS: Record<LatencyTone, string> = {
  green: 'text-positive',
  amber: 'text-amber-500',
  red: 'text-negative',
  none: 'text-muted-foreground',
};

const CSV_HEADER =
  'idx,inputMethod,outcome,keyToPostMs,postToVenueSentMs,venueRttMs,ackToPaintMs,totalMs';

function csvCell(v: number | null): string {
  return v == null ? '' : String(Math.round(v * 100) / 100);
}

/** The `L` dump payload: header + the last `limit` rows, newest LAST. */
export function latencyCsv(
  rows: readonly LatencyRow[],
  limit = LATENCY_CSV_LIMIT,
): string {
  const tail = rows.slice(Math.max(0, rows.length - limit));
  const lines = tail.map((r, i) =>
    [
      String(i),
      r.inputMethod,
      r.outcome,
      csvCell(r.keyToPostMs),
      csvCell(r.postToVenueSentMs),
      csvCell(r.venueRttMs),
      csvCell(r.ackToPaintMs),
      csvCell(r.totalMs),
    ].join(','),
  );
  return [CSV_HEADER, ...lines].join('\n');
}

/**
 * Dump the last 50 entries to the clipboard as CSV (§6, for the speed-demon
 * lane). Exported for the hotkey listener to call on `L` — this module
 * deliberately registers NO key: the registry is the listener's, and two
 * owners of one binding is how a key silently stops working.
 *
 * Resolves false when there is nothing to dump or the clipboard refused.
 */
export async function dumpLatencyCsv(
  rows: readonly LatencyRow[] = collectLatencyRows(),
): Promise<boolean> {
  if (rows.length === 0) return false;
  const csv = latencyCsv(rows);
  try {
    const clip = typeof navigator !== 'undefined' ? navigator.clipboard : undefined;
    if (!clip?.writeText) return false;
    await clip.writeText(csv);
    return true;
  } catch {
    return false;
  }
}

// ── Component ────────────────────────────────────────────────────────────

export type AdverseGuardState = 'off' | 'armed' | 'tripped';

export interface LatencyHudProps {
  /** 'full' = the context-band strip; 'mini' = the execution-band header. */
  variant?: 'full' | 'mini';
  /** Lane liveness, wired by the band (§6). */
  tickAgeMs?: number | null;
  laneCandidate?: string | null;
  adverseGuard?: AdverseGuardState;
  /** Rows to render instead of reading the live rings (fixtures, storybook). */
  rows?: readonly LatencyRow[];
  /** Live re-read cadence in ms; 0 disables the timer (tests, fixtures). */
  refreshMs?: number;
  className?: string;
}

function msCell(v: number | null): string {
  return v == null ? '—' : `${Math.round(v)} ms`;
}

const SEP = '│';

export function LatencyHud({
  variant = 'full',
  tickAgeMs = null,
  laneCandidate = null,
  adverseGuard = 'off',
  rows: rowsProp,
  refreshMs = 1000,
  className,
}: LatencyHudProps) {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (rowsProp || refreshMs <= 0) return;
    const t = setInterval(() => setTick((n) => n + 1), refreshMs);
    return () => clearInterval(t);
  }, [rowsProp, refreshMs]);

  const rows = useMemo(
    () => rowsProp ?? collectLatencyRows(),
    // `tick` is the live re-read trigger; the rings are mutable module state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rowsProp, tick],
  );
  const summary = useMemo(() => summarizeLatency(rows), [rows]);
  const latest = summary.latest;
  const tone = latencyBudgetTone(latest?.totalMs ?? null);

  const [copied, setCopied] = useState<'ok' | 'fail' | null>(null);
  const onDump = useCallback(() => {
    void dumpLatencyCsv(rows).then((ok) => setCopied(ok ? 'ok' : 'fail'));
  }, [rows]);

  if (variant === 'mini') {
    return (
      <span
        data-testid="latency-hud-mini"
        data-tone={tone}
        className={cn('font-mono text-[10px] tabular-nums', className)}
        title="Sniper latency — total / p50 / p95 (press L to dump CSV)"
      >
        <span className={TONE_CLASS[tone]} data-testid="latency-hud-total">
          {msCell(latest?.totalMs ?? null)}
        </span>
        <span className="text-muted-foreground" data-testid="latency-hud-summary">
          {' '}
          · p50 {summary.p50 ?? '—'} · p95 {summary.p95 ?? '—'} · n {summary.n}
        </span>
        {tickAgeMs != null && (
          <span className="text-muted-foreground" data-testid="latency-hud-tick-age">
            {' '}
            · tick {Math.round(tickAgeMs)} ms
          </span>
        )}
      </span>
    );
  }

  return (
    <div
      data-testid="latency-hud"
      data-tone={tone}
      className={cn('font-mono text-[10px] tabular-nums leading-tight', className)}
    >
      <div className="flex flex-wrap items-center gap-x-1.5">
        <span className="text-muted-foreground">key→POST</span>
        <span data-testid="latency-key-to-post">{msCell(latest?.keyToPostMs ?? null)}</span>
        <span className="text-muted-foreground/50">{SEP}</span>
        <span className="text-muted-foreground">POST→venue_sent</span>
        <span data-testid="latency-post-to-venue-sent">
          {msCell(latest?.postToVenueSentMs ?? null)}
        </span>
        <span className="text-muted-foreground/50">{SEP}</span>
        <span className="text-muted-foreground">venue RTT</span>
        <span data-testid="latency-venue-rtt">{msCell(latest?.venueRttMs ?? null)}</span>
        <span className="text-muted-foreground/50">{SEP}</span>
        <span className="text-muted-foreground">ack→paint</span>
        <span data-testid="latency-ack-to-paint">{msCell(latest?.ackToPaintMs ?? null)}</span>
        <span className="text-muted-foreground/50">{SEP}</span>
        <span className="text-muted-foreground">total</span>
        <span className={cn('font-semibold', TONE_CLASS[tone])} data-testid="latency-hud-total">
          {msCell(latest?.totalMs ?? null)}
        </span>
        <span className="ml-2 text-muted-foreground" data-testid="latency-hud-summary">
          p50 {summary.p50 ?? '—'} · p95 {summary.p95 ?? '—'} · n {summary.n}
        </span>
      </div>
      <div className="mt-0.5 flex flex-wrap items-center gap-x-1.5 text-muted-foreground">
        <span data-testid="latency-hud-tick-age">
          tick age {tickAgeMs == null ? '—' : `${Math.round(tickAgeMs)} ms`}
        </span>
        <span className="text-muted-foreground/50">{SEP}</span>
        <span data-testid="latency-hud-lane">lane {laneCandidate ?? '—'}</span>
        <span className="text-muted-foreground/50">{SEP}</span>
        <span
          data-testid="latency-hud-adverse-guard"
          data-state={adverseGuard}
          className={
            adverseGuard === 'armed'
              ? 'text-positive'
              : adverseGuard === 'tripped'
                ? 'text-amber-500'
                : undefined
          }
        >
          guard {adverseGuard}
        </span>
        <button
          type="button"
          onClick={onDump}
          data-testid="latency-hud-dump"
          className="ml-auto rounded border border-border px-1 hover:bg-muted/40"
          title="Dump the last 50 entries as CSV (hotkey L)"
        >
          L · dump{copied === 'ok' ? ' ✓' : copied === 'fail' ? ' ✗' : ''}
        </button>
      </div>
    </div>
  );
}

export default LatencyHud;
